import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type QuickSearchOpenOptions, useQuickSearchBarEvents} from '@github-ui/quick-search/bar-events'
import {Link} from '@github-ui/react-router'
import {isUIService} from '@github-ui/runtime-environment'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {getPrimaryKeybinding} from '@github-ui/ui-commands'
import {useMediaQuery} from '@github-ui/use-media-query'
import {CommandPaletteIcon, SearchIcon} from '@primer/octicons-react'
import {ButtonGroup, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, startTransition, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore} from 'react'

import {NavButtonItem, NavLinkItem} from '../../primitives/NavItemLink'
import {shouldHideTopNav, useTopNavConfig, useTopNavSearchState} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import styles from './Search.module.css'

const QuickSearch = lazy(async () => {
  const module = await import('@github-ui/quick-search')
  return {default: module.QuickSearch}
})

const FeedbackDialog = lazy(async () => {
  const module = await import('@github-ui/blackbird-dialogs/FeedbackDialog')
  return {default: module.FeedbackDialog}
})

async function preloadQuickSearch() {
  const module = await import('@github-ui/quick-search')
  module.preloadQuickSearch()
}

// Mirrors the breakpoint in Search.module.css that toggles between the wide
// button group and the compact icon button.
const COMPACT_VIEWPORT_QUERY = '(max-width: 1024px)'

export type SearchProps = {
  show?: boolean
  showCommandPalette?: boolean
  isSearchPage?: boolean
  isJumpToSearch?: boolean
  searchContext?: Record<string, string>
  locationSearch?: string
}

export function SearchPlaceholder({
  isSearchPage,
  isLegacySearch,
  locationSearch = '',
}: {
  isSearchPage?: boolean
  isLegacySearch?: boolean
  locationSearch?: string
}) {
  const urlParams = new URLSearchParams(locationSearch)
  const query = urlParams.get('q')?.trim()

  if (query && isSearchPage) {
    return (
      <span className={clsx(styles.placeholder, styles.text, styles.value, isLegacySearch && styles.searchText)}>
        {query}
      </span>
    )
  }

  return (
    <span className={clsx(styles.placeholder, styles.text, styles.value)}>
      Type <kbd className={styles.kbd}>{getPrimaryKeybinding('global-navigation:search')}</kbd> to search
    </span>
  )
}

export function Search({
  show,
  isJumpToSearch,
  showCommandPalette,
  isSearchPage,
  searchContext,
  locationSearch: initialLocationSearch,
}: SearchProps) {
  const locationSearch = useLocationSearch(initialLocationSearch)
  const {sendItemClick} = useGlobalNavAnalytics('QUICK_SEARCH')
  const {searchOpen, quickSearchOpen, topNavHidden} = useTopNavSearchState()
  const {setSearchOpen, setQuickSearchOpen, setTopNavHidden, loadCopilotChat} = useTopNavConfig()
  const searchKeybinding = getPrimaryKeybinding('global-navigation:search') || '/'
  const quickSearchAriaLabel = `Open quick search dialog, type ${searchKeybinding} to search`
  const showNewQuickSearch = isUIService
    ? isFeatureEnabled('new_quick_search_ui_service')
    : isFeatureEnabled('new_quick_search_dotcom')

  const [searchTextBlocks, setSearchTextBlocks] = useState<SearchTextBlock[]>([])
  const [searchLoaded, setSearchLoaded] = useState(false)
  // The quick-search panel anchors (top-flush, matching left/width) to the header
  // search region, which is a parent element, so we resolve it from the DOM.
  const quickSearchAnchorRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    quickSearchAnchorRef.current = document.querySelector<HTMLElement>('[data-testid="top-nav-center"]')
  }, [])

  // The header renders two search triggers (the regular button group and a
  // compact icon button) that are toggled by CSS at 1024px (see
  // Search.module.css). When the overlay closes, return focus to whichever
  // trigger the current viewport actually shows so focus lands back on the
  // button the user sees. This matters most for keyboard opens: the command
  // handler calls `.click()` on the compact button, which does not move focus
  // to it, so Primer's fallback (restore previous focus) would otherwise drop
  // focus to <body> — and on wide screens the compact button is hidden anyway.
  const largeSearchButtonRef = useRef<HTMLButtonElement>(null)
  const smallSearchButtonRef = useRef<HTMLButtonElement>(null)
  const quickSearchReturnFocusRef = useRef<HTMLElement>(null)
  const isCompactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY)

  useEffect(() => {
    quickSearchReturnFocusRef.current = isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current
  }, [isCompactViewport])

  const scope = searchContext?.scope || ''
  const searchPageQuery = isSearchPage ? (new URLSearchParams(locationSearch).get('q')?.trim() ?? '') : ''

  // Reflects edits made inside the QuickSearch dialog. It persists after the
  // dialog closes; `null` means "not edited", so we fall back to the query
  // derived from the URL.
  const [editedQuery, setEditedQuery] = useState<string | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [retainQuickSearchScrollPosition, setRetainQuickSearchScrollPosition] = useState(false)
  // What QuickSearch reopens with — the entered text is kept on every page.
  const seededQuery = editedQuery ?? searchPageQuery
  // What the header button shows — only surfaced on the search page; elsewhere
  // the button keeps its placeholder.
  const displayedQuery = isSearchPage ? seededQuery : ''
  // Kick off the module import (and its data pre-warm) immediately on mount so
  // users who open via keyboard shortcut get instant results, not just hover users.
  useEffect(() => {
    if (showNewQuickSearch) void preloadQuickSearch()
  }, [showNewQuickSearch])

  const handleQuickSearchIntent = useCallback(() => {
    loadCopilotChat()
    void preloadQuickSearch()
  }, [loadCopilotChat])

  const showQuickSearch = useCallback(() => {
    void preloadQuickSearch()
    startTransition(() => {
      setQuickSearchOpen(true)
    })
  }, [setQuickSearchOpen])

  const openQuickSearch = useCallback(() => {
    setRetainQuickSearchScrollPosition(false)
    quickSearchReturnFocusRef.current = isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current
    showQuickSearch()
  }, [isCompactViewport, showQuickSearch])

  const openQuickSearchFromEvent = useCallback(
    (options?: QuickSearchOpenOptions) => {
      setRetainQuickSearchScrollPosition(options?.retainScrollPosition ?? false)
      quickSearchReturnFocusRef.current =
        options?.returnTarget ?? (isCompactViewport ? smallSearchButtonRef.current : largeSearchButtonRef.current)
      showQuickSearch()
    },
    [isCompactViewport, showQuickSearch],
  )

  const closeQuickSearch = useCallback(() => {
    // Close synchronously (not in a transition). Deferring the close leaves the
    // overlay painted while React prepares the closed tree, and an urgent
    // re-render committed in that window can briefly reveal the underlying
    // "Search or jump to…" button before ours repaints with the correct query.
    setQuickSearchOpen(false)
  }, [setQuickSearchOpen])

  const handleFeedbackClose = useCallback(() => {
    setFeedbackOpen(false)
    quickSearchReturnFocusRef.current?.focus()
  }, [])

  const handleSearchLoad = useCallback(() => setSearchLoaded(true), [])

  const handleQuickSearchSubmit = useCallback(
    (_query: string) => {
      sendItemClick('SEARCH_SUBMISSION')
    },
    [sendItemClick],
  )

  const handleQuickSearchSuggestionSelect = useCallback(
    (type: 'jump-to' | 'search' | 'autocomplete') => {
      sendItemClick('SUGGESTION_SELECTION', type)
    },
    [sendItemClick],
  )

  const {navigateToSearch} = useQuickSearchBarEvents({
    enabled: showNewQuickSearch,
    searchPageQuery,
    onSetQuery: setEditedQuery,
    onRequestOpen: openQuickSearchFromEvent,
    onRequestFeedback: () => {
      setQuickSearchOpen(false)
      setFeedbackOpen(true)
    },
  })

  const getHref = () => {
    if (isUIService) {
      return scope ? {href: `/search?${new URLSearchParams({q: scope}).toString()}`} : {href: '/search'}
    } else {
      return {}
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(locationSearch)

    const hideTopNav =
      shouldHideTopNav(urlParams, isSearchPage ?? false, isJumpToSearch ?? false) && !searchTextBlocks.length

    if (topNavHidden !== hideTopNav) {
      setTopNavHidden(hideTopNav)
    }
  }, [locationSearch, setTopNavHidden, topNavHidden, isSearchPage, isJumpToSearch, searchTextBlocks])

  if (!show) {
    return null
  }

  if (isJumpToSearch && !showNewQuickSearch) {
    return (
      <JumpToSearch
        showCommandPalette={showCommandPalette}
        isSearchPage={isSearchPage}
        locationSearch={locationSearch}
      />
    )
  }
  return (
    <>
      <ButtonGroup
        className={clsx(styles.searchButtonGroup, isSearchPage && styles.searchPage, searchOpen && styles.hideButtons)}
      >
        {showNewQuickSearch ? (
          <NavButtonItem
            ref={largeSearchButtonRef}
            className={styles.searchButton}
            onClick={openQuickSearch}
            onFocus={handleQuickSearchIntent}
            onMouseEnter={handleQuickSearchIntent}
            icon={SearchIcon}
            analyticsKey="SEARCH"
            aria-label={quickSearchAriaLabel}
            aria-haspopup="dialog"
            aria-expanded={quickSearchOpen}
            linkText={
              displayedQuery ? (
                <span className={clsx(styles.placeholder, styles.text, styles.value)}>{displayedQuery}</span>
              ) : (
                <span className={clsx(styles.placeholder, styles.text, styles.value)}>
                  Type <kbd className={styles.kbd}>{searchKeybinding}</kbd> to search
                </span>
              )
            }
          />
        ) : (
          <NavLinkItem
            className={styles.searchButton}
            aria-busy={!searchLoaded}
            onClick={() => {
              if (!isUIService && searchLoaded) {
                setSearchOpen(true)
              }
            }}
            onFocus={loadCopilotChat}
            onMouseEnter={loadCopilotChat}
            icon={SearchIcon}
            {...getHref()}
            analyticsKey="SEARCH"
            aria-label="Search or jump to…"
            linkText={
              searchTextBlocks && searchTextBlocks.length > 0 ? (
                searchTextBlocks.map((block, index) => {
                  return (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <span key={`value-${index}`} className={clsx(styles.text, styles[block.type])}>
                      {block.value}
                    </span>
                  )
                })
              ) : (
                <SearchPlaceholder isSearchPage={isSearchPage} locationSearch={locationSearch} />
              )
            }
          />
        )}
        {showCommandPalette && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      {showNewQuickSearch ? (
        <NavButtonItem
          ref={smallSearchButtonRef}
          icon={SearchIcon}
          aria-label={quickSearchAriaLabel}
          commandId="global-navigation:search"
          onClick={openQuickSearch}
          onFocus={handleQuickSearchIntent}
          onMouseEnter={handleQuickSearchIntent}
          className={styles.smallSearchButton}
          analyticsKey="SEARCH"
        />
      ) : (
        <NavLinkItem
          icon={SearchIcon}
          aria-label="Search or jump to…"
          commandId="global-navigation:search"
          {...getHref()}
          onClick={() => {
            if (isUIService) return
            if (searchLoaded) {
              setSearchOpen(true)
            }
          }}
          onFocus={loadCopilotChat}
          onMouseEnter={loadCopilotChat}
          className={styles.smallSearchButton}
          analyticsKey="SEARCH"
        />
      )}
      {!showNewQuickSearch && (
        <BlackbirdSearch
          open={searchOpen}
          onLoad={handleSearchLoad}
          onClose={closed => {
            setSearchOpen(!closed)
          }}
          onSearchBlocksChange={setSearchTextBlocks}
        />
      )}
      {showNewQuickSearch && quickSearchOpen && (
        <Suspense fallback={null}>
          <QuickSearch
            isOpen={quickSearchOpen}
            anchorRef={quickSearchAnchorRef}
            retainScrollPosition={retainQuickSearchScrollPosition}
            initialQuery={seededQuery}
            returnFocusRef={quickSearchReturnFocusRef}
            onClose={closeQuickSearch}
            onSearchSubmit={handleQuickSearchSubmit}
            onSuggestionSelect={handleQuickSearchSuggestionSelect}
            onFeedbackClick={() => {
              setQuickSearchOpen(false)
              setFeedbackOpen(true)
            }}
            onQueryChange={setEditedQuery}
            onNavigateToSearch={navigateToSearch}
            searchContext={searchContext}
            isSearchPage={isSearchPage}
          />
        </Suspense>
      )}
      {showNewQuickSearch && feedbackOpen && (
        <Suspense fallback={null}>
          <FeedbackDialog onClose={handleFeedbackClose} />
        </Suspense>
      )}
    </>
  )
}

function JumpToSearch({showCommandPalette, isSearchPage, locationSearch = ''}: SearchProps) {
  // Mirrors the same condition that causes SearchPlaceholder to render
  // `.searchText` for legacy search, so we can set a class directly on the
  // ButtonGroup instead of relying on `:has(.searchText)`.
  const hasSearchText = Boolean(isSearchPage && new URLSearchParams(locationSearch).get('q')?.trim())
  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <span className="AppHeader-search">
      <ButtonGroup className={clsx(styles.placeholderButtonGroup, hasSearchText && styles.hasSearchText)}>
        <NavLinkItem
          className={clsx(styles.searchButton, styles.placeholderButton)}
          icon={SearchIcon}
          href="/search"
          analyticsKey="SEARCH"
          aria-label="Search or jump to…"
          linkText={<SearchPlaceholder isSearchPage={isSearchPage} isLegacySearch locationSearch={locationSearch} />}
        />
        {showCommandPalette && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      <IconButton
        as={Link}
        icon={SearchIcon}
        to="/search"
        aria-label="Search or jump to…"
        className={clsx(styles.mobilePlaceholderButton)}
      />
    </span>
  )
}

// The <qbsearch-input> element (and its companion CSRF token input) is
// rendered once by Rails inside `.js-global-bar` and moved into the React
// global nav on mount. We cache the reference at module scope to balance two
// constraints:
//
// 1. Never duplicate the node. A previous attempt used `cloneNode(true)`,
//    which copied every descendant `id` (`#custom-scopes-dialog`, etc.).
//    Duplicate ids broke primer's dialog-helper close-by-id handler, leaving
//    `body.has-modal-disable-scroll` stuck on after closing the save-query
//    dialog. See https://github.com/github/primer/issues/6379.
//
// 2. Survive React remounts. When the global nav unmounts (e.g. after
//    browser back), the qbsearch-input is detached from the document along
//    with its wrapper, so `document.querySelector('qbsearch-input')` returns
//    null on the next mount and the search disappears. Holding a JS
//    reference here keeps the detached node alive so we can re-attach it.
//    See https://github.com/github/github-ui/pull/10988.
let cachedSearchInput: SearchInputElement | null = null
let cachedSuggestionsTokenInput: Element | null = null

function BlackbirdSearch(props: {
  open?: boolean
  onClose?: (closed: boolean) => void
  onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
  onLoad?: () => void
}) {
  const {onLoad, onClose, open, onSearchBlocksChange} = props
  const searchInputWrapperRef = useRef<HTMLDivElement | null>(null)

  // `loaded` is really "are we on the client yet" — the qbsearch-input element can only be wired
  // up after hydration. Derive it from useRuntimeEnvironment (SSR-safe via useSyncExternalStore)
  // instead of a set-state-in-effect, so this effect's only job is to notify the parent on mount.
  const {isBrowser} = useRuntimeEnvironment()
  const [hasChildDialogOpen, setHasChildDialogOpen] = useState(false)

  useEffect(() => {
    onLoad?.()
  }, [onLoad])

  const onInitialize = useCallback(() => {
    // Prefer the cached reference (survives remounts). Fall back to the
    // Rails-rendered source on first mount. See the `cachedSearchInput`
    // comment above for the two issues this pattern addresses.
    const searchInputCandidate = cachedSearchInput ?? document.querySelector('qbsearch-input')
    if (!searchInputCandidate || !isSearchInputElement(searchInputCandidate)) {
      return
    }
    cachedSearchInput = searchInputCandidate
    // `appendChild` on an already-parented node moves it — the DOM only
    // allows one parent per node, so no cloning happens here.
    searchInputWrapperRef.current?.appendChild(searchInputCandidate)

    const suggestionsTokenInput =
      cachedSuggestionsTokenInput ?? document.querySelector('.js-data-jump-to-suggestions-path-csrf')
    if (suggestionsTokenInput) {
      cachedSuggestionsTokenInput = suggestionsTokenInput
      searchInputWrapperRef.current?.appendChild(suggestionsTokenInput)
    }
  }, [])

  useSearchElement(searchInputWrapperRef, {
    loaded: isBrowser,
    onClose,
    open,
    onSearchBlocksChange,
    onInitialize,
    onChildDialogChange: setHasChildDialogOpen,
  })

  // Visible when search is open OR when a child dialog (like feedback) is open
  const isVisible = open || hasChildDialogOpen

  return (
    <div
      className={clsx({
        'd-flex width-full': open,
        'd-none': !isVisible,
        // When child dialog is open but search is closed, use absolute positioning
        // to keep child dialogs visible without affecting layout
        [styles.childDialogOpen]: hasChildDialogOpen && !open,
      })}
      ref={searchInputWrapperRef}
    />
  )
}

/**
 * A partial definition of the qbsearch-input web component's element interface
 */
export type SearchInputElement = Element & {
  handleExpand: () => void
  closeDialog: () => void
  syncRichButtonText: () => void
}

function isSearchInputElement(element: Element | null): element is SearchInputElement {
  return element !== null && 'handleExpand' in element && 'closeDialog' in element && 'syncRichButtonText' in element
}

/**
 * A block of text in the search input, representing either a filter, constant text, entity, or value
 */
export type SearchTextBlock = {
  type: 'filter' | 'constant' | 'entity' | 'value'
  value: string
}

function subscribeToLocationSearch(onStoreChange: () => void) {
  window.addEventListener('popstate', onStoreChange)
  return () => {
    window.removeEventListener('popstate', onStoreChange)
  }
}

function getLocationSearchSnapshot() {
  return window.location.search
}

function useLocationSearch(initialLocationSearch?: string) {
  return useSyncExternalStore(
    subscribeToLocationSearch,
    getLocationSearchSnapshot,
    () => initialLocationSearch ?? ssrSafeLocation.search ?? '',
  )
}

function useSearchElement(
  containerRef: React.RefObject<HTMLElement | null>,
  options: {
    loaded?: boolean
    open?: boolean
    onClose?: (closed: boolean) => void
    onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
    onInitialize?: () => void
    onChildDialogChange?: (hasOpenDialog: boolean) => void
  },
) {
  const searchInputRef = useRef<SearchInputElement | null>(null)
  const {onInitialize, onChildDialogChange} = options
  const container = containerRef.current

  const initializeSearch = useCallback(() => {
    if (!options.loaded || !container || searchInputRef.current) {
      return
    }

    onInitialize?.()

    // Move the search input into the center section of the navigation bar
    const searchInput = container.querySelector('qbsearch-input')
    if (!searchInput || !isSearchInputElement(searchInput)) {
      return
    }

    searchInputRef.current = searchInput

    searchInputRef.current.syncRichButtonText()
  }, [container, options.loaded, onInitialize])

  useEffect(() => {
    if (options.open) {
      initializeSearch()
      searchInputRef.current?.handleExpand()
    }
  }, [options.open, initializeSearch])

  useEffect(() => {
    const onCloseSearch = () => {
      options.onClose?.(true)
    }

    const onOpenSearch = () => {
      onChildDialogChange?.(false)
      options.onClose?.(false)
    }

    const onUpdateText = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      options.onSearchBlocksChange?.(event.detail.searchTextBlocks as SearchTextBlock[])
    }

    // Initialize search input on mount
    initializeSearch()

    // Always listen for the updateText event so we get updates to the search text when the nav is hidden on `/search`
    document.addEventListener('qbsearch-input:updateText', onUpdateText)

    const searchInput = searchInputRef.current
    if (!searchInput) {
      return () => {
        document.removeEventListener('qbsearch-input:updateText', onUpdateText)
      }
    }

    document.addEventListener('qbsearch-input:close', onCloseSearch)
    document.addEventListener('qbsearch-input:expand', onOpenSearch)

    // Use MutationObserver to detect when child dialogs open or close
    // This handles the feedback dialog which opens after the search dialog closes.
    //
    // The qbsearch-input subtree mutates frequently (e.g. suggestion lists update on every
    // keystroke), so coalesce bursts of mutations into a single check per animation frame and skip
    // redundant state updates. This keeps the observer callback off the presentation-delay path of
    // search interactions.
    let rafId = 0
    let lastHasOpenDialog: boolean | undefined
    const checkForOpenDialog = () => {
      rafId = 0
      const hasOpenDialog = Boolean(
        searchInput.querySelector('dialog[open]') || searchInput.querySelector('modal-dialog[open]'),
      )
      if (hasOpenDialog !== lastHasOpenDialog) {
        lastHasOpenDialog = hasOpenDialog
        onChildDialogChange?.(hasOpenDialog)
      }
    }
    const observer = new MutationObserver(() => {
      if (rafId) return
      rafId = requestAnimationFrame(checkForOpenDialog)
    })

    observer.observe(searchInput, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    })

    return () => {
      document.removeEventListener('qbsearch-input:close', onCloseSearch)
      document.removeEventListener('qbsearch-input:expand', onOpenSearch)
      document.removeEventListener('qbsearch-input:updateText', onUpdateText)
      if (rafId) cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [initializeSearch, onChildDialogChange, options])
}
